import React from 'react';
import { HbrIcon, HbrSpinner, HbrTag } from '@harbor/elements-react-standalone';
import { arrowSquareOut } from '@harbor/elements/icons';
import StbButton from '../../security-toolbox-ui/StbButton';
import {
  DcloudSession,
  DcloudSessionState,
  formatDcloudDate,
} from '../../../support/dcloudService';

const STATE_LABELS: Record<DcloudSessionState, string> = {
  active: 'Running',
  scheduled: 'Scheduled',
  ended: 'Ended',
  error: 'Error',
};

const STATE_SENTIMENTS: Record<DcloudSessionState, string> = {
  active: 'success',
  scheduled: 'info',
  ended: 'info',
  error: 'danger',
};

export type DcloudSessionsTableProps = {
  sessions: DcloudSession[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  /** Previous sessions are read-only upstream; only re-scheduling is offered. */
  variant?: 'current' | 'previous';
  busySessionId?: number | null;
  onEdit?: (session: DcloudSession) => void;
  onEnd?: (session: DcloudSession) => void;
  onScheduleAgain?: (session: DcloudSession) => void;
};

const DcloudSessionsTable: React.FC<DcloudSessionsTableProps> = ({
  sessions,
  loading = false,
  error = null,
  emptyMessage = 'No sessions found.',
  variant = 'current',
  busySessionId = null,
  onEdit,
  onEnd,
  onScheduleAgain,
}) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-gray-600">
        <HbrSpinner size="small" />
        Loading dCloud sessions...
      </div>
    );
  }

  if (error) {
    return <p className="p-4 text-sm text-amber-800">{error}</p>;
  }

  if (!sessions.length) {
    return <p className="p-4 text-sm text-gray-600">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2 font-semibold">Session</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold">Start</th>
            <th className="px-3 py-2 font-semibold">End</th>
            <th className="px-3 py-2 font-semibold text-right">Actions</th>
          </tr>
        </thead>

        <tbody>
          {sessions.map((session, index) => {
            const busy = busySessionId != null && busySessionId === session.id;

            return (
              <tr
                key={session.id ?? `${session.name}-${index}`}
                className="border-b border-gray-100 align-top"
              >
                <td className="px-3 py-2">
                  <div className="font-medium">{session.name}</div>
                  {session.demoName && session.demoName !== session.name && (
                    <div className="text-xs text-gray-500">{session.demoName}</div>
                  )}
                  {session.scenario && (
                    <div className="text-xs text-gray-500">{`Scenario: ${session.scenario}`}</div>
                  )}
                  {session.owner && (
                    <div className="text-xs text-gray-500">{`Owner: ${session.owner}`}</div>
                  )}
                </td>

                <td className="px-3 py-2">
                  <HbrTag size="small" sentiment={STATE_SENTIMENTS[session.state] as any}>
                    {STATE_LABELS[session.state]}
                  </HbrTag>
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  {formatDcloudDate(session.start)}
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  {formatDcloudDate(session.stop)}
                </td>

                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {session.launchUrl && (
                      <a
                        href={session.launchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm"
                      >
                        Open
                        <HbrIcon asset={arrowSquareOut} />
                      </a>
                    )}

                    {variant === 'current' && onEdit && session.canEdit && (
                      <StbButton
                        role="update"
                        size="xsmall"
                        disabled={busy || !session.id}
                        onClick={async () => onEdit(session)}
                      >
                        Edit
                      </StbButton>
                    )}

                    {variant === 'current' && onEnd && (
                      <StbButton
                        role="delete"
                        size="xsmall"
                        disabled={busy || !session.id}
                        loading={busy}
                        onClick={async () => onEnd(session)}
                      >
                        End
                      </StbButton>
                    )}

                    {variant === 'previous' && onScheduleAgain && (
                      <StbButton
                        role="add"
                        size="xsmall"
                        disabled={!session.demoId}
                        onClick={async () => onScheduleAgain(session)}
                      >
                        Schedule again
                      </StbButton>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DcloudSessionsTable;
